class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Auto-synchronize empty state: if items are present, remove is-empty
    const hasItems = this.querySelector('.cart-item') !== null || (this.querySelector('.cart-drawer__footer') !== null && this.querySelector('.drawer__inner-empty') === null);
    if (hasItems) {
      this.classList.remove('is-empty');
      const itemsContainer = this.querySelector('cart-drawer-items');
      if (itemsContainer) itemsContainer.classList.remove('is-empty');
      const emptyInner = this.querySelector('.drawer__inner-empty');
      if (emptyInner) emptyInner.remove();
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections || !parsedState.sections['cart-drawer']) {
      if (typeof window.htcSyncCartUI === 'function') {
        window.htcSyncCartUI({ openDrawer: true, broadcast: true });
      }
      return;
    }
    this.classList.remove('is-empty');
    const itemsContainer = this.querySelector('cart-drawer-items');
    if (itemsContainer) itemsContainer.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections[section.id]) return;
      const parsedDom = section.selector ? this.getSectionDOM(parsedState.sections[section.id], section.selector) : null;
      const nextHtml = parsedDom ? parsedDom.innerHTML : this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (nextHtml) {
        sectionElement.innerHTML = nextHtml;
        if (parsedDom && parsedDom.hasAttribute('data-server-qty')) {
          sectionElement.setAttribute('data-server-qty', parsedDom.getAttribute('data-server-qty'));
        }
      }
    });

    const hasItemsAfterRender = this.querySelector('.cart-item') !== null;
    window.__htcLastAtomicPaintAt = Date.now();
    if (hasItemsAfterRender) {
      this.classList.remove('is-empty');
      const newItemsContainer = this.querySelector('cart-drawer-items');
      if (newItemsContainer) newItemsContainer.classList.remove('is-empty');
      const emptyInner = this.querySelector('.drawer__inner-empty');
      if (emptyInner) emptyInner.remove();
      const checkoutBtn = document.getElementById('CartDrawer-Checkout');
      if (checkoutBtn) {
        checkoutBtn.disabled = false;
        checkoutBtn.removeAttribute('disabled');
      }
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      if (typeof window.htcSyncDiscountCookieWithDom === 'function') {
        window.htcSyncDiscountCookieWithDom();
      }
      if (hasItemsAfterRender && window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        try { window.Shopify.PaymentButton.init(); } catch (e) {}
      }
      if (typeof window.htcBroadcastCartMutation === 'function') {
        window.htcBroadcastCartMutation(typeof window.htcGetDomCartCount === 'function' ? window.htcGetDomCartCount() : undefined);
      }
      if (document.getElementById('main-cart-items') && typeof window.htcSyncCartUI === 'function') {
        window.htcSyncCartUI({ sections: parsedState.sections, openDrawer: false, broadcast: false });
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
